import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';

const toISO = (d) => d.toISOString().slice(0, 10);

export const OwnerCalendar = ({ api }) => {
  const today = new Date();
  const [viewDate, setViewDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [booked, setBooked] = useState(new Set());
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/owner/venue');
      setBooked(new Set(res.data.venue?.availability?.booked_dates || []));
    } catch {
      toast.error('Failed to load availability');
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const toggle = (iso) => {
    const next = new Set(booked);
    if (next.has(iso)) next.delete(iso); else next.add(iso);
    setBooked(next);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/owner/availability', { booked_dates: Array.from(booked) });
      setDirty(false);
      toast.success('Availability updated — your public page reflects it immediately');
    } catch {
      toast.error('Failed to save availability');
    } finally {
      setSaving(false);
    }
  };

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayISO = toISO(today);
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(<div key={`e${i}`} />);
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const isPast = iso < todayISO;
    const isBooked = booked.has(iso);
    cells.push(
      <button
        key={iso}
        disabled={isPast}
        onClick={() => toggle(iso)}
        data-testid={`owner-cal-day-${iso}`}
        className={`aspect-square flex items-center justify-center text-[13px] tabular-nums rounded-md transition-colors
          ${isPast ? 'text-stone-300 cursor-not-allowed' : ''}
          ${isBooked ? 'bg-[#9E4040] text-white font-semibold' : ''}
          ${!isPast && !isBooked ? 'hover:bg-[#F3EFEB] text-[#1A3626]' : ''}`}
      >
        {d}
      </button>
    );
  }

  return (
    <div className="bg-white border border-[#E5DCCF] rounded-2xl p-6" data-testid="owner-calendar">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h3 className="font-wvc-display text-2xl text-[#0A1710]">Availability Calendar</h3>
          <p className="text-xs text-[#1A3626]/60 mt-1">Tap a date to block or unblock it. Blocked dates show as booked on your public page and in guest estimates.</p>
        </div>
        <Button onClick={save} disabled={!dirty || saving} className="bg-[#1A3626] hover:bg-[#254834] rounded-full" data-testid="owner-cal-save-btn">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
        </Button>
      </div>
      <div className="flex items-center justify-between mb-4 max-w-md">
        <button onClick={() => setViewDate(new Date(year, month - 1, 1))} className="p-2 hover:bg-[#F3EFEB] rounded-full" data-testid="owner-cal-prev">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <p className="font-wvc-display text-lg text-[#0A1710]">{viewDate.toLocaleString('en-IN', { month: 'long', year: 'numeric' })}</p>
        <button onClick={() => setViewDate(new Date(year, month + 1, 1))} className="p-2 hover:bg-[#F3EFEB] rounded-full" data-testid="owner-cal-next">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 max-w-md text-center">
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
          <div key={d} className="text-[10px] uppercase tracking-wider text-[#1A3626]/40 font-bold py-1">{d}</div>
        ))}
        {cells}
      </div>
      <div className="flex items-center gap-5 mt-5 text-[11px] text-[#1A3626]/60">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-[#9E4040]" /> Blocked / booked</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded border border-[#E5DCCF]" /> Available</span>
      </div>
    </div>
  );
};
